import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes
 * @param {...any} inputs - Class names to merge
 * @returns {string} Merged class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency to GBP
 * @param {number} amount - Amount in pence or pounds
 * @param {boolean} inPence - Whether amount is in pence
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, inPence = false) {
  const value = inPence ? amount / 100 : amount;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}

/**
 * Format a date value for use in <input type="date"> (expects yyyy-MM-dd).
 * Handles ISO strings (e.g. "2026-01-24T00:00:00.000Z"), Date objects, and already-formatted strings.
 * @param {Date|string|null|undefined} value - Date from API or state
 * @returns {string} yyyy-MM-dd or empty string
 */
export function formatDateForInput(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  try {
    const date = typeof value === 'string' ? new Date(value) : value;
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

/**
 * Format date to UK format
 * @param {Date|string} date - Date to format
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Formatted date string
 */
export function formatDate(date, includeTime = false) {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (includeTime) {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  }
  
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
  }).format(d);
}

/**
 * Convert a money amount in pounds to integer pence with safe rounding.
 * @param {number|string|null|undefined} amount
 * @returns {number} Integer pence
 */
export function toPence(amount) {
  if (amount == null || amount === '') return 0;
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(num) || !Number.isFinite(num)) return 0;
  return Math.round(num * 100);
}

/**
 * Convert integer pence back to pounds.
 * @param {number|null|undefined} pence
 * @returns {number} Pounds (2dp when formatted)
 */
export function fromPence(pence) {
  if (pence == null || Number.isNaN(pence) || !Number.isFinite(pence)) return 0;
  return pence / 100;
}

/**
 * Calculate VAT amount (UK standard rate: 20%)
 * @param {number} amount - Net amount (in pounds)
 * @param {number} rate - VAT rate (default 20%)
 * @returns {number} VAT amount
 */
export function calculateVAT(amount, rate = 20) {
  return (amount * rate) / 100;
}

/**
 * Calculate VAT from a net amount expressed in pence, using integer arithmetic.
 * @param {number} netPence - Net amount in pence
 * @param {number} rate - VAT rate percentage (e.g. 20 for 20%)
 * @returns {number} VAT amount in pence
 */
export function calculateVatFromNetPence(netPence, rate = 20) {
  if (!rate || !Number.isFinite(rate)) return 0;
  const safeNet = Number.isFinite(netPence) ? netPence : 0;
  return Math.round((safeNet * rate) / 100);
}

/**
 * Generate initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export function getInitials(name) {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

